import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { CaretLeft, UserCircle } from '@phosphor-icons/react'
import { useI18n } from '@/i18n'
import { roster, useDeviceInfo, type TeamMember } from '@/offline'
import { usePhoneSession } from '@/auth/pin/phoneSession'
import { Banner, BilingualLabel, Button, EmptyState, Logo, PinPad, Skeleton } from '@/ui'
import { cn } from '@/lib/cn'
import { env } from '@/lib/env'
import { LanguageToggle } from '../components/LanguageToggle'
import { listWords } from '../listWords'
import { V1Unlock } from '@/features/gate/v1/V1Unlock'
import { useStoreScope } from '@/features/store/v1/ByScope'

/**
 * Offline unlock (F4.1-t): pick who is working, enter that person's PIN. Verified on the phone
 * against the PIN checks delivered with the roster. A supervisor unlocking an operator's phone
 * picks himself — every command then carries his id.
 */
export function PinUnlockPage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const { actor, unlock } = usePhoneSession()
  const info = useDeviceInfo()
  const kind = info.registration?.team.kind
  const words = listWords(kind)
  const team = useLiveQuery(() => roster.team(), [])
  const config = useLiveQuery(() => roster.config(), [])
  const gateConfig = useLiveQuery(() => roster.gateConfig(), [])
  const storeScope = useStoreScope()
  const [selected, setSelected] = useState<TeamMember | null>(null)
  const [message, setMessage] = useState<{ tone: 'error' | 'warn'; text: string } | null>(null)
  const [errorSignal, setErrorSignal] = useState(0)
  const [lockedUntil, setLockedUntil] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  const [checking, setChecking] = useState(false)

  const from = (location.state as { from?: string } | null)?.from ?? '/m'
  // Multi-village phones: a village phone asks which village after the PIN (it goes straight on
  // when the person has only this one).
  const afterUnlock = (): [string, { state?: { from: string } }] =>
    kind === 'VILLAGE' ? ['/m/gate/village', { state: { from } }] : [from, {}]
  const pinLength = Number(config?.pin_length) || 4
  const locked = lockedUntil > now

  useEffect(() => {
    if (!locked) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [locked])

  if (info.loaded && (!info.registration || info.freshness === 'missing'))
    return <Navigate to="/m/setup" replace />
  if (actor) {
    const [to, opts] = afterUnlock()
    return <Navigate to={to} state={opts.state} replace />
  }

  const onPin = async (pin: string) => {
    if (!selected) return
    setChecking(true)
    const res = await unlock(selected.user_id, pin)
    setChecking(false)
    if (res.ok) {
      const [to, opts] = afterUnlock()
      navigate(to, { replace: true, ...opts })
      return
    }
    setErrorSignal((n) => n + 1)
    if (res.reason === 'wrong_pin')
      setMessage({ tone: 'error', text: t('pin.wrong', { left: res.triesLeft }) })
    else if (res.reason === 'locked_out') {
      setLockedUntil(Date.now() + res.retryInMs)
      setNow(Date.now())
      setMessage(null)
    } else if (res.reason === 'no_pin') setMessage({ tone: 'warn', text: t('pin.noPinSet') })
    else setMessage({ tone: 'error', text: t(listWords(kind).noTeam) })
  }

  // Seva Mandir V1 (every team kind): the map's PIN boards. The scope comes from the roster
  // config on a village phone and from the store list on a site phone; while either is still
  // unknown the phone takes V1, the default.
  const v1 = kind === 'SITE' ? (storeScope?.v1 ?? true) : (gateConfig?.v1 ?? true)
  if (v1 && team && team.length > 0)
    return (
      <V1Unlock
        team={team}
        village={info.registration?.team.name ?? ''}
        pinLength={pinLength}
        unlock={unlock}
        onDone={() => {
          const [to, opts] = afterUnlock()
          navigate(to, { replace: true, ...opts })
        }}
      />
    )

  return (
    <main className="mx-auto flex min-h-dvh max-w-screen-sm flex-col bg-paper px-4 pb-8">
      <div className="pt-safe flex items-center justify-between py-4">
        <Logo />
        <LanguageToggle />
      </div>

      {info.freshness === 'expired' && (
        <Banner tone="warn" className="mb-3">
          {t(words.expired)}
        </Banner>
      )}
      {info.freshness === 'stale' && env.rosterMaxAgeDays > 1 && (
        <Banner tone="info" className="mb-3">
          {t(words.stale)}
        </Banner>
      )}

      {!selected ? (
        <>
          <h1 className="mb-4 text-[24px] leading-tight">
            <BilingualLabel
              k="pin.who"
              layout="stacked"
              secondaryClassName="text-[15px] font-sans"
            />
          </h1>
          {team === undefined ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </div>
          ) : team.length === 0 ? (
            <EmptyState title={t(words.noTeam)} />
          ) : (
            <ul className="flex flex-col gap-2">
              {team.map((m) => (
                <li key={m.user_id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(m)
                      setMessage(null)
                    }}
                    className="flex min-h-[64px] w-full items-center gap-3 rounded-card border border-rule bg-surface px-4 text-left hover:border-ink/40"
                  >
                    <UserCircle
                      size={36}
                      weight="duotone"
                      className="shrink-0 text-amber-dark"
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[17px] font-semibold text-ink">
                        {m.name}
                      </span>
                      <span className="block truncate font-mono text-[11px] uppercase tracking-label text-muted">
                        {m.roles.map((r) => r.replace(/_/g, ' ')).join(' · ')}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center">
          <button
            type="button"
            onClick={() => setSelected(null)}
            className="mb-4 inline-flex items-center gap-1 self-start text-[14px] text-muted hover:text-ink"
          >
            <CaretLeft aria-hidden="true" /> {t('pin.switchUser')}
          </button>
          <div className="mb-1 text-[20px] font-semibold text-ink">{selected.name}</div>
          <h1 className="mb-5 text-[16px] font-sans text-muted">
            <BilingualLabel k="pin.enter" />
          </h1>
          <div
            className={cn('mb-4 w-full', !message && !locked && 'invisible')}
            aria-live="assertive"
          >
            {locked ? (
              <Banner tone="warn">
                {t('pin.lockedOut', { seconds: Math.ceil((lockedUntil - now) / 1000) })}
              </Banner>
            ) : (
              message && <Banner tone={message.tone}>{message.text}</Banner>
            )}
          </div>
          <PinPad
            length={pinLength}
            onComplete={(p) => void onPin(p)}
            disabled={locked || checking}
            errorSignal={errorSignal}
            label={t('pin.enter')}
          />
          {import.meta.env.DEV && (
            <Button variant="ghost" size="sm" className="mt-6" onClick={() => navigate('/m/setup')}>
              {t('device.title')}
            </Button>
          )}
        </div>
      )}
    </main>
  )
}
